/**
 * A-4 채점 결과 → 생기부 핸드오프.
 *
 * 채점 결과(학생별 근거 인용·잘한 점·보완할 점·종합 의견)를 다중 생기부 생성 폼이 그대로 읽는
 * `batchRestore` 형태로 바꾼다. /create-saenggibu 는 location.state.batchRestore 를 받아
 * Step1_ExcelUpload 에서 학생 목록·지시사항을 복원한다(기록 "이어하기"와 같은 경로).
 *
 * 원칙
 *  - 생기부에는 점수를 쓰지 않는다. 점수·등급·배점·채점 기준 문장은 조립할 때 지워서 아예 넘기지 않는다.
 *  - 답안 원문·파일명 같은 채점 메타는 넘기지 않는다. 학생이 실제로 쓴 말은 근거 인용으로만 넘긴다.
 *  - 순수 함수: 컴포넌트 상태를 모르고 입력만 받는다 → 단위 테스트 가능.
 */

import { getScoringStudentName, resolveAnswerTextForResult } from './scoringResultMeta';
import { isPlaceholderStudentName } from '../../utils/studentNamePolicy';

export const SAENGGIBU_HANDOFF_CATEGORY = '교과';
export const SAENGGIBU_HANDOFF_SOURCE = 'scoring';
export const SCORING_HANDOFF_MAX_RECORDS = 30;

const MAX_CONTENT_CHARS = 2400;
const MAX_STUDENT_CHARS = 15000;
const MAX_QUOTES = 4;
const MAX_QUOTE_CHARS = 160;

const SCORE_PATTERNS = [
  /\d+(?:\.\d+)?\s*\/\s*\d+(?:\.\d+)?\s*점?/,
  /\d+(?:\.\d+)?\s*점(?!검|차|심|선|자)/,
  /[A-Ea-e]\s*등급|\d\s*등급|등급(?:을|으로|이|은|에)/,
  /만점|배점|감점|총점|득점|최고점|최저점|점수/,
  /채점\s*기준|루브릭|평가\s*기준(?:에|을|상)|성취\s*수준\s*[A-E상중하]/,
];

const hasScoreText = (text) => SCORE_PATTERNS.some(pattern => pattern.test(text));

const splitSentences = (text) => String(text || '')
  .replace(/\r/g, '')
  .split(/(?<=[.!?。])\s+|\n+/)
  .map(part => part.trim())
  .filter(Boolean);

/** 점수·등급·배점·채점 기준이 들어간 문장을 통째로 지운다(일부만 지우면 문장이 깨진다). */
export const stripScoreSentences = (text) => splitSentences(text)
  .filter(sentence => !hasScoreText(sentence))
  .join(' ')
  .trim();

const cleanList = (list) => (Array.isArray(list) ? list : [])
  .map(item => stripScoreSentences(item))
  .filter(Boolean);

const getItems = (result) => {
  const items = Array.isArray(result?.item_scores) ? result.item_scores : [];
  const fromQuestions = (Array.isArray(result?.question_results) ? result.question_results : [])
    .flatMap(question => (Array.isArray(question?.item_scores) ? question.item_scores : []));
  return [...items, ...fromQuestions];
};

const collectTopics = (result) => {
  const names = [
    ...(result?.question_results || []).map(q => q?.question_title || (q?.question_id ? `${q.question_id}번 문항` : '')),
    ...getItems(result).map(item => item?.item_name),
  ];
  return [...new Set(names.map(name => stripScoreSentences(String(name || ''))).filter(Boolean))].slice(0, 8);
};

const collectQuotes = (result) => {
  const quotes = [
    ...getItems(result).flatMap(item => (Array.isArray(item?.evidence_quotes) ? item.evidence_quotes : [])),
    ...(result?.question_results || []).map(q => q?.answer_excerpt),
  ];
  const seen = new Set();
  return quotes
    .map(quote => String(quote || '').replace(/\s+/g, ' ').trim())
    .filter(quote => quote.length >= 6 && !hasScoreText(quote))
    .filter(quote => (seen.has(quote) ? false : seen.add(quote)))
    .slice(0, MAX_QUOTES)
    .map(quote => (quote.length > MAX_QUOTE_CHARS ? `${quote.slice(0, MAX_QUOTE_CHARS - 1)}…` : quote));
};

export const isUsableScoringResult = (result) => Boolean(result) && !result.error && !result.is_blank_forced;

/**
 * 한 학생의 채점 결과를 생기부 "기존 관찰 내용" 텍스트로 만든다. 점수 문장은 들어가지 않는다.
 */
export const buildHandoffContentForResult = (result) => {
  if (!isUsableScoringResult(result)) return '';
  const sections = [];

  const topics = collectTopics(result);
  if (topics.length > 0) sections.push(`평가한 내용: ${topics.join(', ')}`);

  const quotes = collectQuotes(result);
  if (quotes.length > 0) sections.push(`학생이 쓴 말\n${quotes.map(quote => `- "${quote}"`).join('\n')}`);

  const strengths = cleanList(result.strengths);
  if (strengths.length > 0) sections.push(`잘한 점: ${strengths.join(', ')}`);

  const improvements = cleanList(result.improvements);
  if (improvements.length > 0) sections.push(`보완할 점: ${improvements.join(', ')}`);

  const feedback = stripScoreSentences(result.detailed_feedback);
  if (feedback) sections.push(`종합 관찰: ${feedback}`);

  const comment = stripScoreSentences(result.teacher_comment);
  if (comment) sections.push(`채점 때 적은 의견: ${comment}`);

  const text = sections.join('\n\n').trim();
  return text.length > MAX_CONTENT_CHARS ? `${text.slice(0, MAX_CONTENT_CHARS - 1)}…` : text;
};

export const buildHandoffTeacherInstruction = (titles = []) => {
  const list = (Array.isArray(titles) ? titles : [titles]).map(t => String(t || '').trim()).filter(Boolean);
  const head = list.length > 1
    ? `아래 학생별 내용은 서술형 평가 ${list.length}건(${list.slice(0, 5).join(', ')}${list.length > 5 ? ' 등' : ''})의 채점 결과입니다.`
    : list.length === 1
      ? `아래 학생별 내용은 "${list[0]}" 서술형 채점 결과입니다.`
      : '아래 학생별 내용은 AI 서술형 채점 결과입니다.';
  return [
    head,
    '점수·등급과 "평가 기준", "채점 기준" 같은 말은 쓰지 마세요.',
    '"학생이 쓴 말"과 잘한 점·보완할 점을 근거로, 학생이 실제로 보인 사고 과정과 표현을 관찰 중심으로 서술하고,',
    '"채점 때 적은 의견"은 뜻만 참고하고 그대로 베끼지 마세요.',
  ].join(' ');
};

const normalizeKey = (name) => String(name || '')
  .normalize('NFC')
  .replace(/\([^)]*\)|\[[^\]]*\]/g, '')
  .replace(/[^0-9A-Za-z가-힣]/g, '')
  .toLowerCase();

const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.getMonth() + 1}월 ${date.getDate()}일`;
};

const recordTitle = (record) => String(record?.title || record?.subject || '서술형 평가').trim();

const buildRestore = ({ students, titles, sourceFileName, recordIds = [] }) => ({
  batchRestore: {
    source: SAENGGIBU_HANDOFF_SOURCE,
    category: SAENGGIBU_HANDOFF_CATEGORY,
    wordCount: 1500,
    students,
    teacherInstruction: buildHandoffTeacherInstruction(titles),
    referenceText: '',
    sourceFileName,
    sourceFileSize: 0,
    sourceFileMeta: {
      source: SAENGGIBU_HANDOFF_SOURCE,
      student_count: students.length,
      ...(recordIds.length > 0 ? { scoring_record_count: recordIds.length } : {}),
    },
    structureAnalysis: {
      total_students: students.length,
      detected_columns: ['학생명', '채점 결과'],
    },
  },
});

/**
 * 채점 기록 1건 → /create-saenggibu 로 넘길 location.state. 넘길 학생이 없으면 null.
 */
export const buildSaenggibuHandoffState = ({
  results = [],
  cachedAnswerTexts = [],
  batchStudents = [],
  rubricTitle = '',
} = {}) => {
  const list = Array.isArray(results) ? results : [];
  const students = list
    .map((result, index) => {
      const answer = resolveAnswerTextForResult({
        cachedAnswerTexts,
        results: list,
        result,
        resultIndex: index,
        batchStudents,
      });
      const name = getScoringStudentName(result, answer?.student_name || `학생 ${index + 1}`);
      const content = buildHandoffContentForResult(result);
      if (!content) return null;
      return {
        id: `scoring_${index + 1}`,
        name: name || `학생 ${index + 1}`,
        existing_content: content,
        content,
        characteristics: cleanList(result?.strengths).join(', '),
        confidence: 1,
        warnings: [],
      };
    })
    .filter(Boolean);

  if (students.length === 0) return null;
  const title = String(rubricTitle || '').trim();
  return buildRestore({
    students,
    titles: title ? [title] : [],
    sourceFileName: title ? `채점 결과 · ${title}` : '서술형 채점 결과',
  });
};

/**
 * 채점 기록 여러 건 → 학생별로 평가를 시간순으로 모은 batchRestore.
 *
 * - canonicalName(name): 반 명단에서 맞춘 이름(별칭 포함)을 돌려주면 그 이름으로 묶는다.
 * - "학생 3" 같은 자리표시 이름, 오류·백지 결과는 뺀다.
 * - 한 기록 안에서 같은 이름이 둘이면 묶지 않고 따로 두고 확인 문구를 단다.
 */
export const buildMultiRecordHandoffState = (records = [], { canonicalName } = {}) => {
  const sorted = (Array.isArray(records) ? records : [])
    .filter(Boolean)
    .slice(0, SCORING_HANDOFF_MAX_RECORDS)
    .map((record, order) => ({ record, order }))
    .sort((a, b) => (new Date(a.record.created_at || 0) - new Date(b.record.created_at || 0)) || (a.order - b.order))
    .map(({ record }) => record);

  const byKey = new Map();
  const skipped = { placeholder: 0, blank: 0 };
  const recordsUsed = [];

  sorted.forEach((record) => {
    const results = Array.isArray(record.results) ? record.results : [];
    const cachedAnswerTexts = Array.isArray(record.cached_answer_texts) ? record.cached_answer_texts : [];
    const label = [recordTitle(record), formatDate(record.created_at)].filter(Boolean).join(' · ');
    const seenInRecord = new Map();
    let used = false;

    results.forEach((result, index) => {
      if (!isUsableScoringResult(result)) {
        if (result?.is_blank_forced) skipped.blank += 1;
        return;
      }
      const answer = resolveAnswerTextForResult({ cachedAnswerTexts, results, result, resultIndex: index });
      const rawName = getScoringStudentName(result, answer?.student_name || '');
      if (!rawName || isPlaceholderStudentName(rawName)) {
        skipped.placeholder += 1;
        return;
      }
      const name = (canonicalName && canonicalName(rawName)) || rawName;
      const baseKey = normalizeKey(name);
      if (!baseKey) return;
      const content = buildHandoffContentForResult(result);
      if (!content) return;

      const repeat = (seenInRecord.get(baseKey) || 0) + 1;
      seenInRecord.set(baseKey, repeat);
      const key = repeat > 1 ? `${baseKey}#${record.id || 'r'}#${repeat}` : baseKey;
      if (!byKey.has(key)) {
        byKey.set(key, {
          name: repeat > 1 ? `${name} (${repeat})` : name,
          sections: [],
          recordIds: new Set(),
          sameNameInRecord: repeat > 1,
          strengths: [],
        });
      }
      const entry = byKey.get(key);
      if (repeat === 2) {
        const first = byKey.get(baseKey);
        if (first) first.sameNameInRecord = true;
      }
      entry.sections.push(`[${label}]\n${content}`);
      entry.recordIds.add(record.id || label);
      entry.strengths.push(...cleanList(result.strengths));
      used = true;
    });
    if (used) recordsUsed.push(record);
  });

  if (byKey.size === 0) return null;
  const totalRecords = recordsUsed.length;
  const students = [...byKey.values()].map((entry, index) => {
    let content = '';
    for (const section of entry.sections) {
      const next = content ? `${content}\n\n${section}` : section;
      if (next.length > MAX_STUDENT_CHARS) break;
      content = next;
    }
    const warnings = [];
    if (totalRecords > 1 && entry.recordIds.size < totalRecords) {
      warnings.push(`${totalRecords}건 중 ${entry.recordIds.size}건 참여`);
    }
    if (entry.sameNameInRecord) warnings.push('같은 이름이 한 기록에 둘 있어요. 다른 학생인지 확인해 주세요');
    return {
      id: `scoring_multi_${index + 1}`,
      name: entry.name,
      existing_content: content,
      content,
      characteristics: [...new Set(entry.strengths)].slice(0, 3).join(', '),
      confidence: warnings.length > 0 ? 0.6 : 1,
      warnings,
      scoring_record_count: entry.recordIds.size,
    };
  });

  const titles = [...new Set(recordsUsed.map(recordTitle))];
  return {
    ...buildRestore({
      students,
      titles,
      sourceFileName: totalRecords > 1 ? `채점 결과 ${totalRecords}건` : `채점 결과 · ${titles[0] || '서술형 평가'}`,
      recordIds: recordsUsed.map(record => record.id).filter(Boolean),
    }),
    summary: { records: totalRecords, students: students.length, ...skipped },
  };
};
